import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { listNodes } from '@/api/nodes'
import GuardianAlphaConfigPanel from '@/components/GuardianAlphaConfigPanel'
import type { GuardianAlphaEditorTab } from '@/components/guardian-alpha/types'
import type { NodeSummary } from '@/types'

const NODE_PICKER_LIMIT = 1000
const MANUAL_OPERATING_ENABLED =
  import.meta.env.VITE_MANUAL_OPERATING_ENABLED === 'true'

function editorTab(value: string | null): GuardianAlphaEditorTab {
  if (value === 'config' || value === 'input') return value
  return 'settings'
}

export default function GuardianAlphaConfigPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedNodeId, setSelectedNodeId] = useState(
    () => searchParams.get('node') ?? '',
  )
  const [selectedGuardianNodeId, setSelectedGuardianNodeId] = useState(
    () => searchParams.get('guardianNode') ?? '',
  )
  const [activeTab, setActiveTab] = useState<GuardianAlphaEditorTab>(
    () => editorTab(searchParams.get('tab')),
  )
  const [nodes, setNodes] = useState<NodeSummary[]>([])
  const [nodesLoading, setNodesLoading] = useState(true)

  useEffect(() => {
    listNodes({ limit: NODE_PICKER_LIMIT })
      .then((response) => setNodes(response.items))
      .finally(() => setNodesLoading(false))
  }, [])

  useEffect(() => {
    setSelectedNodeId(searchParams.get('node') ?? '')
    setSelectedGuardianNodeId(searchParams.get('guardianNode') ?? '')
    setActiveTab(editorTab(searchParams.get('tab')))
  }, [searchParams])

  function selectNode(nodeId: string) {
    setSelectedNodeId(nodeId)
    setSelectedGuardianNodeId('')
    setActiveTab('settings')
    const next = new URLSearchParams(searchParams)
    if (nodeId) next.set('node', nodeId)
    else next.delete('node')
    next.delete('guardianNode')
    next.delete('tab')
    setSearchParams(next, { replace: true })
  }

  function selectGuardianNode(guardianNodeId: string) {
    setSelectedGuardianNodeId(guardianNodeId)
    setActiveTab('settings')
    const next = new URLSearchParams(searchParams)
    if (guardianNodeId) next.set('guardianNode', guardianNodeId)
    else next.delete('guardianNode')
    next.delete('tab')
    setSearchParams(next, { replace: true })
  }

  function selectTab(tab: GuardianAlphaEditorTab) {
    setActiveTab(tab)
    const next = new URLSearchParams(searchParams)
    if (tab === 'settings') next.delete('tab')
    else next.set('tab', tab)
    setSearchParams(next, { replace: true })
  }

  return (
    <div>
      <h1 style={{ margin: '0 0 20px', fontSize: 22, fontWeight: 700 }}>
        GuardianAlpha Config
      </h1>
      <GuardianAlphaConfigPanel
        nodes={nodes}
        nodesLoading={nodesLoading}
        selectedNodeId={selectedNodeId}
        onSelectNode={selectNode}
        selectedGuardianNodeId={selectedGuardianNodeId}
        onSelectGuardianNode={selectGuardianNode}
        activeTab={activeTab}
        onSelectTab={selectTab}
        manualOperatingEnabled={MANUAL_OPERATING_ENABLED}
      />
    </div>
  )
}
